import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { rolesService } from "../services/rolesService";
import type { AccessRole } from "../types/access.types";

/**
 * Loads the roles catalog (`GET /internal/roles`) with policies embedded. Shared by every "Access &
 * permissions" section: the effective-access matrix and the deny-first evaluator resolve assigned role ids
 * against this list. Settings has no Redux slice, so all HTTP stays in the hook (the local state + service
 * pattern).
 */
export function useRoles() {
  const [roles, setRoles] = useState<AccessRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasNoAccess, setHasNoAccess] = useState(false);
  /**
   * The load failed for a reason that is NOT a 401/403 — a 5xx, a timeout, a dropped connection.
   *
   * It exists because `roles` is `[]` in that case exactly as it is on a 403, and consumers that DERIVE
   * something from the catalog cannot tell the two apart from the data alone. `hasNoAccess` alone left the
   * commonest failure indistinguishable from an authoritative empty answer.
   *
   * Recorded IN ADDITION to the rethrow, never instead of it: callers that already handle the rejection
   * (`AccessDataProvider`, `useUsersAccess`) keep behaving exactly as before.
   */
  const [hasError, setHasError] = useState(false);

  // Monotonic load counter: concurrent `load()` calls (initial mount + a section's reload) can resolve out of
  // order, and the slower response must never overwrite the fresher one. Only the latest invocation may write
  // state. Bumped on unmount so an in-flight load can't set state on an unmounted hook.
  const loadSeq = useRef(0);
  useEffect(
    () => () => {
      loadSeq.current += 1;
    },
    [],
  );

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setIsLoading(true);
    setHasNoAccess(false);
    setHasError(false);
    try {
      const data = await rolesService.listAll();
      if (seq !== loadSeq.current) return; // stale response — a newer load owns the state
      setRoles(data);
    } catch (error) {
      if (seq !== loadSeq.current) return;
      if (HttpApiService.isAccessDeniedError(error)) {
        setHasNoAccess(true);
        setRoles([]);
        return;
      }
      // Recorded, THEN rethrown: the flag lets a consumer render "unknown" instead of an empty catalog,
      // while the rejection still reaches whoever was already handling it.
      setHasError(true);
      throw error;
    } finally {
      if (seq === loadSeq.current) setIsLoading(false);
    }
  }, []);

  // Memoized so the value forwarded by AccessDataProvider is stable across renders (consumers re-render only
  // on a real change). Adding a field here is the single place to touch — the provider just forwards this.
  return useMemo(
    () => ({ roles, isLoading, hasNoAccess, hasError, load }),
    [roles, isLoading, hasNoAccess, hasError, load],
  );
}
